import { ArrowForwardIosOutlined } from "@mui/icons-material";
import { Box, Button, Checkbox, Collapse, Divider, Link, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { termsAndConditionsInfo } from "../assets/termsConditions";
import { PlanNameFromType, setPlanSelected } from "../redux/features/planStateSlice";
import { PlanPaymentFixed } from "./PlanPaymentFixed";
import { PlanPaymentFree } from "./PlanPaymentFree";
import { PlanPaymentPayAsYouGo } from "./PlanPaymentPayAsYouGo";
import { PromoCodeDisccount } from "./PromoCodeDisccount";
import { BaseDialog } from "./common/BaseDialog";
import { TermsAndConditions } from "./termsAndConditions";
import { colors } from "./ui/colors";

const styles = {
  titleSx: {
    textAlign: "center",
  },
};

const getTotalPrice = (planSelected, discount) => {
  const price = Number(planSelected?.price?.amount ?? 0);

  if (planSelected?.trialDetails) {
    return Number(planSelected.trialDetails.pricing_scheme.fixed_price.value ?? 0);
  }

  const total = price - Number(discount ?? 0);
  return total >= 0 ? total : 0;
};

export const PlanCheckout = ({ setSubscriptionState }) => {
  const { planSelected } = useSelector((state) => state.planState);

  const { company, firstName, lastName, address, state, city, country, zipCode, subscriptionInfo } = useSelector((state) => state.userState);
  const currentPlanExpiresAt = [subscriptionInfo?.subscription, ...(subscriptionInfo?.subscriptions ?? []).map((item) => item?.subscription)]
    .filter(Boolean)
    .map((subscription) => {
      const expDate = subscription?.expDate ? new Date(subscription.expDate) : null;
      const stillValid =
        expDate &&
        expDate.getTime() > Date.now() &&
        subscription?.plan?.product?.section === "flow_subscription" &&
        subscription?.paypalId !== "free_trial";
      return stillValid ? expDate : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const [openTermsAndConditions, setOpenTermsAndConditions] = useState(false);
  const [check, setCheck] = useState(false);
  const [discount, setDisccount] = useState(0);

  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isFreePlan = planSelected?.name?.toLowerCase() === "free" || planSelected?.price?.amount === 0;
  const chargeLater = Boolean(currentPlanExpiresAt) && !isFreePlan && planSelected?.billingType !== "PAY_AS_YOU_GO" && !planSelected?.isFreeTrial;
  const subtotal = Number(planSelected?.price?.amount ?? 0);
  const total = getTotalPrice(planSelected, discount);

  const termsDialog = {
    marginLeft: isMobile ? "-10px" : "0px",
    width: isMobile ? "115%" : "100%",
  };

  const fullName = useMemo(() => {
    return [firstName, lastName].filter(Boolean).join(" ");
  }, [firstName, lastName]);

  const fullAddress = useMemo(() => {
    return [address, city, state, country, zipCode].filter(Boolean).join(", ");
  }, [address, city, state, country, zipCode]);

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",

        borderRadius: 0,
        p: { xs: 2, sm: 3, md: 0 },
        bgcolor: "transparent",
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.03em", mb: 0.5 }}>
            Checkout
          </Typography>

          <Typography variant="body2">Review your organization information and continue with the payment.</Typography>
        </Box>

        {/* Selected card + Cost card */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1.1fr 0.9fr",
            },
            gap: { xs: 2, md: 3 },
            alignItems: "stretch",
          }}
        >
          {/* Selected Plan */}
          <Box
            sx={{
              borderRadius: 2.5,
              p: 2.5,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              height: "100%",
            }}
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="overline" color={colors.orange.base} sx={{ fontWeight: 700 }}>
                    Selected plan
                  </Typography>

                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {PlanNameFromType[planSelected?.type] ?? planSelected?.name ?? "Plan"}
                  </Typography>
                </Box>

                <Button
                  endIcon={<ArrowForwardIosOutlined />}
                  sx={{
                    bgcolor: colors.orange.base,
                    px: 1,
                    color: "#ffff",
                    "&:hover": {
                      bgcolor: colors.orange[600],
                      fontSize: "12",
                      fontWeight: 600,
                    },
                  }}
                  size="small"
                  onClick={() => {
                    dispatch(setPlanSelected({ planSelected: undefined }));
                  }}
                >
                  Edit
                </Button>
              </Box>

              <Divider />

              <Stack spacing={0.75}>
                <Typography variant="subtitle2" sx={{ color: colors.orange.base, fontWeight: 700 }}>
                  Billing information
                </Typography>

                {company?.businessName && <Typography variant="body2">{company.businessName}</Typography>}

                {fullName && <Typography variant="body2">{fullName}</Typography>}

                {fullAddress && (
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.6,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {fullAddress}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>

          {/* Cost / Total */}
          <Box
            sx={{
              borderRadius: 2.5,
              p: 2.5,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Cost
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Subtotal
                </Typography>
                <Typography variant="body2">${subtotal}</Typography>
              </Box>

              {planSelected?.billingType !== "PAY_AS_YOU_GO" && (
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Promo Code Discount
                  </Typography>
                  <Typography variant="body2">${discount}</Typography>
                </Box>
              )}

              {planSelected?.trialDetails && (
                <Box mt={1}>
                  <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: "bold", mb: 0.75 }}>
                    Trial Details
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body2">Duration</Typography>
                    <Typography variant="body2">
                      {planSelected.trialDetails.frequency.interval_count} {planSelected.trialDetails.frequency.interval_unit.toLowerCase()}
                      {planSelected.trialDetails.total_cycles > 1 ? "s" : ""}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body2">Price</Typography>
                    <Typography variant="body2">${planSelected.trialDetails.pricing_scheme.fixed_price.value}</Typography>
                  </Box>

                  <Typography variant="body2" color="text.warning" sx={{ mt: 1 }}>
                    Note: This plan includes a trial period. When this trial finishes, the normal price will be applied.
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Typography variant="h5" color={colors.orange.base} sx={{ fontWeight: "bold" }}>
                  Total:
                </Typography>

                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  ${total}
                </Typography>
              </Box>

              {chargeLater && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 1, lineHeight: 1.4 }}>
                  Your current plan stays active until{" "}
                  {currentPlanExpiresAt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}. The charge happens
                  automatically when that plan expires.
                </Typography>
              )}

              {planSelected?.billingType === "PAY_AS_YOU_GO" && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic", lineHeight: 1.4 }}>
                  Note: The base price and monthly usage will be charged automatically on the 1st of each month. The first base price charge will be
                  prorated based on the remaining days in the month.
                </Typography>
              )}

              {!isFreePlan && planSelected?.billingType !== "PAY_AS_YOU_GO" && <PromoCodeDisccount setDisccount={setDisccount} />}
            </Stack>
          </Box>
        </Box>

        {/* Terms */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            borderRadius: 2.5,
            p: 2,
            bgcolor: "background.paper",
          }}
        >
          <Checkbox
            checked={check}
            onChange={() => setCheck((prev) => !prev)}
            sx={{
              p: 0.25,
              color: colors.orange.base,
              "&.Mui-checked": {
                color: colors.orange.base,
              },
            }}
          />

          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            I have read and agree to the Flownect{" "}
            <Link underline="hover" sx={{ cursor: "pointer", fontWeight: 700 }} component={RouterLink} to="/terms-and-conditions" target="_blank">
              Terms & Conditions.
            </Link>
          </Typography>
        </Box>

        {/* Payment */}
        <Box
          sx={{
            borderRadius: 2.5,
            p: 2.5,
            bgcolor: "background.paper",
            width: "100%",
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5" color={!check ? "text.disabled" : "text.primary"} sx={{ fontWeight: 800 }}>
                Payment method
              </Typography>

              {!check && <Typography variant="body2">Accept the terms and conditions to enable payment.</Typography>}
            </Box>

            <Collapse in={check} unmountOnExit>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: {
                      xs: "100%",
                      sm: 480,
                    },
                    mx: "auto",

                    "& .PlanPaymentPaypal": {
                      width: "100%",
                    },
                    "& .PlanPaymentPaypal > div": {
                      width: "100%",
                    },
                  }}
                >
                  {isFreePlan ? (
                    <PlanPaymentFree setSubscriptionState={setSubscriptionState} plan={planSelected} />
                  ) : planSelected?.billingType === "PAY_AS_YOU_GO" ? (
                    <PlanPaymentPayAsYouGo setSubscriptionState={setSubscriptionState} plan={planSelected} />
                  ) : (
                    <PlanPaymentFixed setSubscriptionState={setSubscriptionState} plan={planSelected} />
                  )}
                </Box>
              </Box>
            </Collapse>
          </Stack>
        </Box>
      </Stack>

      <BaseDialog
        openState={openTermsAndConditions}
        setOpenState={() => {
          setOpenTermsAndConditions(false);
        }}
        fullWidth
        maxWidth="md"
        titleSx={styles.titleSx}
        title={termsAndConditionsInfo.title}
        contentSx={termsDialog}
        content={
          <TermsAndConditions
            setOpenTermsAndConditions={() => {
              setOpenTermsAndConditions(false);
            }}
          />
        }
      />
    </Paper>
  );
};
